import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { Btn, DangerBtn } from '../kit';

/**
 * Vocabulary and plumbing shared by the "Homepage" admin tabs (owner,
 * 2026-10-10): Hero numbers, Trusted by, Success stories, and (2026-10-12)
 * Search chips.
 *
 * Everything goes through the panel dispatcher, section "homepage"
 * (Backend/homepage_content.py): GET content, POST save_hero / save_trusted /
 * save_stories / save_search / reset / upload_logo. Every save and reset
 * answers with the same shape as GET content, so the panel keeps one copy and
 * hands it down.
 */

export const S = 'homepage';

export type Metric = 'universities' | 'tech_offers' | 'countries';
export const METRICS: Metric[] = ['universities', 'tech_offers', 'countries'];

/**
 * Where a metric's "+X NEW!" count comes from (owner, 2026-10-11):
 * refresh (the default) = what the latest data refresh — one load — added,
 * which stays until the next refresh instead of resetting on the 1st; while
 * that refresh added none of a metric, the hero_options.refresh_fallback
 * choice applies. monthly = this calendar month's additions (India time), or
 * — until this month has any — the hero_options.month_fallback month; month =
 * a month the admin picks; window = a rolling window; manual = a typed number.
 * ('auto' is what a rolling window was called before; the backend maps it.)
 */
export type NewMode = 'refresh' | 'monthly' | 'month' | 'window' | 'manual';
export const NEW_MODES: NewMode[] = ['refresh', 'monthly', 'month', 'window', 'manual'];
/** What "monthly" shows while this month has no additions yet for a metric. */
export type MonthFallback = 'latest' | 'previous' | 'none';
export const FALLBACKS: MonthFallback[] = ['latest', 'previous', 'none'];
/** What "refresh" shows while the latest refresh added none of a metric: the
 *  latest refresh that added some, or that refresh's own 0 (the line hides). */
export type RefreshFallback = 'latest' | 'none';
export const REFRESH_FALLBACKS: RefreshFallback[] = ['latest', 'none'];

export interface HeroSetting {
  value_mode: 'live' | 'manual'; value: number | null;
  new_mode: NewMode | 'auto'; new_month: string | null; new_window: string; new_value: number | null;
  show_new: boolean;
}
/** `refresh_gap_hours`: loads closer together than this are one refresh (1–168, default 12). */
export interface HeroOptions {
  month_fallback: MonthFallback; refresh_fallback?: RefreshFallback; refresh_gap_hours?: number;
}

/** One calendar month of additions (homepage_content._month_rows), newest first. */
export interface MonthRow {
  month: string; label: string; current: boolean;
  universities: number; tech_offers: number; countries: number;
  university_names: string[]; country_names: string[];
}
/** What the monthly mode gives for one metric under one fallback. */
export interface MonthlyPick { value: number; month: string; label: string; fallback_used: boolean }
/**
 * One data refresh — a run of loaded technologies with no gap longer than
 * refresh_gap_hours between them (homepage_content.data_refreshes), newest
 * first. `id` = `start`, the first load minute (ISO, UTC without a zone);
 * `label` is '11 Oct 2026' or '9–11 Oct 2026' (India time). A university or
 * country counts in the refresh that loaded its first technology.
 */
export interface RefreshRow {
  id: string; start: string; end: string; label: string; start_label?: string; end_label?: string;
  universities: number; tech_offers: number; countries: number;
  university_names: string[]; country_names: string[];
}
/** What the refresh mode gives for one metric under one fallback (for the saved
 *  gap). `refresh` is the refresh id, null when nothing was ever loaded; `label`
 *  is '11 Oct 2026 refresh' or '… refresh (latest with additions)'. */
export interface RefreshPick { value: number; refresh: string | null; label: string; fallback_used: boolean }
export interface TrustedItem { id: string; name: string; logo: string; enabled: boolean }
/** `layout`: cards (the default since 2026-10-14) | rows | scroll; `more_text`
 *  ends only the rows / scroll banner. `subtitle` ('' = hidden) is missing from
 *  a backend older than 2026-10-14. */
export interface TrustedSetting {
  enabled: boolean; title: string; subtitle?: string; layout: string; more_text: string; items: TrustedItem[];
}
export interface StoryRow { from: string; verb: string; to: string }
export interface StoriesSetting { enabled: boolean; title: string; subtitle: string; rows: StoryRow[] }
/** The keyword chips under the homepage search box and the pool "Surprise Me"
 *  picks from (an empty pool hides that button); off hides the whole row. */
export interface SearchSetting { enabled: boolean; bubbles: string[]; surprise: string[]; surprise_label: string }

/** GET homepage/content (homepage_content.admin_get). */
export interface HomepageData {
  settings: {
    hero: Record<Metric, HeroSetting>; hero_options?: HeroOptions; trusted: TrustedSetting; stories: StoriesSetting;
    /** Missing from a backend older than 2026-10-12. */
    search?: SearchSetting;
  };
  /** The live corpus counts, before any manual number. */
  live: Record<Metric, number>;
  /** {window: {metric: added in that window}} — what each +NEW window would show. */
  deltas: Record<string, Record<Metric, number>>;
  /** The last 24 calendar months (India time), newest first, zeros included. */
  months?: MonthRow[];
  /** 'YYYY-MM' and 'October 2026' — this month, India time. */
  current_month?: string;
  current_month_label?: string;
  /** {fallback: {metric: pick}} — the monthly mode under each fallback, right now. */
  monthly_preview?: Record<string, Record<Metric, MonthlyPick>>;
  /** The latest data refreshes (at most 24), newest first, grouped with the SAVED gap. */
  refreshes?: RefreshRow[];
  /** How many refreshes there are in all (the table lists `refreshes`). */
  refreshes_total?: number;
  /** {refresh fallback: {metric: pick}} — the refresh mode under each fallback, for the saved gap. */
  refresh_preview?: Record<string, Record<Metric, RefreshPick>>;
  /** {latest|none: sentence} — what each refresh fallback does. */
  refresh_fallbacks?: Record<string, string>;
  /** [lowest, highest] hours between two refreshes the server accepts. */
  gap_hours_range?: number[];
  /** What the hero shows right now, with the saved choices applied. `label` is
   *  "11 Oct 2026 refresh", "October 2026", "September 2026 (latest additions)",
   *  "Last 30 days" or "manual"; `refresh` is the refresh id the line uses. */
  shown: Record<Metric, {
    value: number; new: number; show_new: boolean; label?: string; month?: string | null; refresh?: string | null;
  }>;
  metric_labels: Record<Metric, string>;
  windows: Record<string, string>;
  new_modes?: Record<string, string>;
  fallbacks?: Record<string, string>;
  /** {cards|rows|scroll: label}, cards first. */
  layouts: Record<string, string>;
  limits: {
    items: number; rows: number; asset_kb: number; subtitle?: number;
    bubbles?: number; surprise?: number; chip_len?: number; surprise_label_len?: number;
  };
  /** The built-in chips and pool (what Reset puts back). */
  search_defaults?: SearchSetting;
}

/** What each tab gets from the panel. */
export interface TabProps {
  data: HomepageData;
  /** A save or reset answered with fresh content. */
  onSaved: (d: HomepageData) => void;
  /** This tab has (or no longer has) unsaved edits. */
  onDirty: (dirty: boolean) => void;
}

export type Note = { kind: 'error' | 'ok'; text: string } | null;

export const fmt = (n: any): string => Number(n || 0).toLocaleString();

/** Stable React keys for rows that can be added, moved and deleted before a save. */
let seq = 0;
export const nextKey = () => `k${++seq}`;

/** A swap of row i with its neighbour (dir -1 up, +1 down); out of range is a no-op. */
export const move = <T,>(list: T[], i: number, dir: -1 | 1): T[] => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
};

/**
 * A logo value → an <img> src. '' is "show the name as text"; an uploaded
 * logo (/api/v1/site-assets/<id>) lives on the API host, like every other API
 * call; a built-in file (/homepage/logos/…) is the site's own; https:// as is.
 */
export const logoSrc = (logo: string): string => {
  const v = String(logo || '').trim();
  if (!v) return '';
  if (v.startsWith('/api/')) return `${apiService.getApiBaseUrl().replace(/\/+$/, '')}${v}`;
  return v;
};

export const logoKind = (logo: string): string => {
  const v = String(logo || '').trim();
  if (!v) return 'No image — the name shows as text';
  if (v.startsWith('/api/v1/site-assets/')) return 'Uploaded image';
  if (v.startsWith('/homepage/')) return 'Built-in file';
  return 'Linked image';
};

/** A logo in a fixed slot, never stretched; the name as text when there is no
 *  image or it fails to load — the same rule the homepage banner follows. */
export const LogoSlot: React.FC<{ name: string; logo: string; className?: string }> =
  ({ name, logo, className }) => {
  const src = logoSrc(logo);
  const [bad, setBad] = useState(false);
  useEffect(() => { setBad(false); }, [src]);
  return (
    <div className={`flex items-center justify-center overflow-hidden ${className || ''}`} title={name}>
      {src && !bad ? (
        <img src={src} alt={name} title={name} loading="lazy" onError={() => setBad(true)}
          className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="text-xs sm:text-sm font-bold text-grayscale-1200 text-center leading-tight line-clamp-2">
          {name || '—'}
        </span>
      )}
    </div>
  );
};

/** An on/off switch (same look as the Tech URL pages one). */
export const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string }> =
  ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} title={label}
    onClick={() => onChange(!on)}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer
      ${on ? 'bg-primary-500' : 'bg-grayscale-600'}`}>
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform
      ${on ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
  </button>
);

export const Radio: React.FC<{ name: string; checked: boolean; onChange: () => void; children: React.ReactNode }> =
  ({ name, checked, onChange, children }) => (
  <label className="flex items-center gap-2 text-sm text-grayscale-1200 cursor-pointer">
    <input type="radio" name={name} checked={checked} onChange={onChange} className="accent-primary-500" />
    {children}
  </label>
);

/**
 * A form's draft over the saved settings. `dirty` keeps a half-typed edit
 * from being overwritten when the panel's copy changes underneath (another
 * tab saved, Refresh); `settle()` after this tab's own save or reset lets the
 * answer the server sent become the draft.
 */
export function useDraft<Src, D>(source: Src | undefined, from: (s: Src) => D, onDirty: (d: boolean) => void) {
  const [draft, setDraft] = useState<D>(() => from(source as Src));
  const [dirty, setDirty] = useState(false);
  const seededFrom = useRef<Src | undefined>(source);
  useEffect(() => {
    if (!source || dirty || seededFrom.current === source) return;
    seededFrom.current = source;
    setDraft(from(source));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, dirty]);
  useEffect(() => { onDirty(dirty); }, [dirty, onDirty]);
  const edit = (fn: (d: D) => D) => { setDraft(fn); setDirty(true); };
  const discard = () => {
    if (source) setDraft(from(source));
    seededFrom.current = source;
    setDirty(false);
  };
  const settle = () => setDirty(false);
  return { draft, edit, dirty, discard, settle };
}

/** A typed count: '' → null, "1,204" → 1204, anything else (or over max) → an error. */
export const parseCount = (raw: string, max: number, label: string): { value: number | null; error: string } => {
  const t = String(raw ?? '').trim().replace(/,/g, '');
  if (!t) return { value: null, error: '' };
  if (!/^\d+$/.test(t)) return { value: null, error: `${label} must be a whole number` };
  const n = Number(t);
  if (n > max) return { value: null, error: `${label} must be between 0 and ${max.toLocaleString()}` };
  return { value: n, error: '' };
};

/** Save / Discard / Reset to defaults, the same at the foot of every tab. */
export const SaveBar: React.FC<{
  dirty: boolean; busy: '' | 'save' | 'reset'; onSave: () => void; onDiscard: () => void; onReset: () => void;
}> = ({ dirty, busy, onSave, onDiscard, onReset }) => (
  <div className="flex items-center gap-2 flex-wrap pt-1">
    <Btn kind="primary" onClick={onSave} disabled={!dirty || !!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn>
    {dirty && !busy && <Btn onClick={onDiscard}>Discard changes</Btn>}
    {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
    <span className="flex-1" />
    <span title="Puts this block back to the built-in defaults (and drops any unsaved edits here)">
      {busy === 'reset'
        ? <Btn kind="danger" small disabled>Resetting…</Btn>
        : <DangerBtn small onConfirm={onReset}>Reset to defaults</DangerBtn>}
    </span>
  </div>
);

/** The quiet "Preview" frame around a block as the homepage would draw it. */
export const PreviewFrame: React.FC<{ note?: string; dim?: boolean; children: React.ReactNode }> =
  ({ note, dim, children }) => (
  <div className="rounded-lg border border-dashed border-grayscale-400 bg-white p-4 flex flex-col gap-3">
    <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">
      Preview{note ? <span className="normal-case tracking-normal"> · {note}</span> : null}
    </div>
    <div className={dim ? 'opacity-40' : ''}>{children}</div>
  </div>
);
